import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { SPORTS, ENGINES } from "@/lib/biohackerBrand";

/**
 * Sports — the "Sport Intelligence Packages™" hub landing page.
 * Each sport is its own enterprise landing, but they share this parent
 * grid so buyers can quickly scan every package.
 */

export default function Sports() {
  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="sports-page">
      <SiteNav />
      <section className="pt-32 pb-14 border-b border-white/8 max-w-7xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3">
          Sport Intelligence Packages™
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
          Every sport is a packaged<br/>
          <span className="text-white/50">implementation of the OS.</span>
        </h1>
        <p className="mt-6 text-white/70 max-w-2xl">
          Every package runs on the same {`BioHacker Intelligence Cloud™`} and shares the same {`Athlete Intelligence Profile™`}
          — only the activated Intelligence Engines™ and sport-specific decision surfaces change.
        </p>
      </section>

      <section className="py-14 max-w-7xl mx-auto px-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SPORTS.map((s) => (
            <Link key={s.id} to={s.href}
              className="ink-card rounded-2xl overflow-hidden group hover:border-white/25 transition-all"
              data-testid={`sports-hub-${s.id}`}>
              <div className="aspect-video bg-black relative overflow-hidden">
                <video src={s.hero_video} muted loop playsInline autoPlay
                  className="w-full h-full object-cover opacity-50 group-hover:opacity-80 transition-opacity" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050810] via-transparent to-transparent" />
                {s.flagship && (
                  <span className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.25em] font-bold px-2 py-1 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/50 text-fuchsia-200">
                    Flagship
                  </span>
                )}
              </div>
              <div className="p-5">
                <div className="font-display font-black text-lg text-white leading-tight">{s.name}</div>
                <div className="mt-1 text-xs text-white/55 leading-snug">{s.tag}</div>
                {s.hover_metric && (
                  <div
                    className="mt-3 rounded-lg border border-white/8 bg-white/[0.02] p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`sports-hover-metric-${s.id}`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[9px] tracking-[0.2em] uppercase text-white/45">{s.hover_metric.label}</span>
                      <span className="font-mono-num font-black text-white text-sm tabular-nums">{s.hover_metric.value}</span>
                    </div>
                    <div className="text-[9px] font-mono-num text-white/40 mt-0.5">{s.hover_metric.ctx}</div>
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-1">
                  {s.engines.slice(0, 6).map((eid) => {
                    const eng = ENGINES.find((e) => e.id === eid);
                    if (!eng) return null;
                    return (
                      <span key={eid} className="text-[9px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full border border-white/12 text-white/65">
                        {eng.name.replace("™","")}
                      </span>
                    );
                  })}
                  {s.engines.length > 6 && (
                    <span className="text-[9px] text-white/45 px-1.5 py-0.5">+{s.engines.length - 6}</span>
                  )}
                </div>
                <div className="mt-4 text-[10px] uppercase tracking-[0.25em] text-white/45 group-hover:text-white transition-colors flex items-center gap-1">
                  Explore package <ArrowRight className="h-3 w-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
